import apiClient from '@/plugins/axios'

const getItem = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key)
    return item ? JSON.parse(item) : defaultValue
  } catch {
    return defaultValue
  }
}

const setItem = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    console.error('Error saving to localStorage:', error)
  }
}

const removeItem = (key) => {
  try {
    localStorage.removeItem(key)
  } catch (error) {
    console.error('Error removing from localStorage:', error)
  }
}

export const auth = {
  namespaced: true,
  
  state: {
    user: getItem('user', null),
    token: getItem('token', null),
    isAuthenticated: !!getItem('token', null),
    loading: false,
    savedEmail: getItem('savedEmail', ''),
    savedPassword: getItem('savedPassword', '')
  },
  
  getters: {
    user: state => state.user,
    token: state => state.token,
    isAuthenticated: state => state.isAuthenticated,
    isLoading: state => state.loading,
    userRole: state => state.user?.role?.name || null,
    userRoleId: state => state.user?.role?.id || null,
    userName: state => state.user ? `${state.user.first_name} ${state.user.last_name}` : null,
    isAdmin: state => {
      const roleName = state.user?.role?.name?.toLowerCase()
      const roleId = state.user?.role?.id
      return roleName === 'admin' || roleId === 1
    },
    isSuperAdmin: state => {
      const roleName = state.user?.role?.name?.toLowerCase()
      const roleId = state.user?.role?.id
      return roleName === 'super admin' || roleId === 3
    },
    hasRole: state => (roleName) => {
      return state.user?.role?.name?.toLowerCase() === roleName.toLowerCase()
    },
    savedCredentials: state => ({
      email: state.savedEmail,
      password: state.savedPassword
    })
  },
  
  mutations: {
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    
    SET_AUTH_DATA(state, { user, token }) {
      state.user = user
      state.token = token
      state.isAuthenticated = true
      
      // Store in localStorage
      setItem('user', user)
      setItem('token', token)
      
      // Set axios default header
      if (token) {
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`
      }
    },
    
    UPDATE_USER(state, user) {
      state.user = user
      setItem('user', user)
    },
    
    CLEAR_AUTH_DATA(state) {
      state.user = null
      state.token = null
      state.isAuthenticated = false

      // Remove from localStorage
      removeItem('user')
      removeItem('token')

      // Remove axios default header
      delete apiClient.defaults.headers.common['Authorization']
    },

    SET_SAVED_CREDENTIALS(state, { email, password }) {
      state.savedEmail = email
      state.savedPassword = password

      // Store in localStorage
      setItem('savedEmail', email)
      setItem('savedPassword', password)
    },

    CLEAR_SAVED_CREDENTIALS(state) {
      state.savedEmail = ''
      state.savedPassword = ''

      // Remove from localStorage
      removeItem('savedEmail')
      removeItem('savedPassword')
    }
  },
  
  actions: {
    async login({ commit }, { user, token, rememberPassword = false, email = '', password = '' }) {
      commit('SET_LOADING', true)

      try {
        commit('SET_AUTH_DATA', { user, token })

        // Handle remember password
        if (rememberPassword) {
          commit('SET_SAVED_CREDENTIALS', { email, password })
        } else {
          commit('CLEAR_SAVED_CREDENTIALS')
        }

        return { success: true }
      } catch (error) {
        commit('CLEAR_AUTH_DATA')
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async logout({ commit }) {
      commit('SET_LOADING', true)
      
      try {
        // Call logout API
        await apiClient.post('/logout')
      } catch (error) {
        console.error('Logout API error:', error)
        // Continue with logout even if API call fails
      } finally {
        commit('CLEAR_AUTH_DATA')
        commit('SET_LOADING', false)
      }
    },
    
    async checkAuth({ commit, state }) {
      if (!state.token) {
        return false
      }
      
      try {
        // Verify token with server
        const response = await apiClient.get('/me')
        
        if (response.data?.success) {
          commit('UPDATE_USER', response.data.data.user)
          return true
        }

        // Only clear when the server explicitly says the session is invalid.
        // An unreadable body (proxy error page, a PHP notice printed before the
        // JSON) leaves success undefined - that is a broken response, not proof
        // the token is dead, and logging the user out here stranded them.
        if (response.data?.success === false) {
          commit('CLEAR_AUTH_DATA')
        } else {
          console.error('Auth check got an unreadable response:', response.data)
        }

        return false
      } catch (error) {
        console.error('Auth check failed:', error)

        // Only a 401 proves the token is dead. A network blip, timeout or a 500
        // must not log the user out - that used to strand them on a blank page.
        if (error.response?.status === 401) {
          commit('CLEAR_AUTH_DATA')
        }

        return false
      }
    },
    
    async updateProfile({ commit }, userData) {
      try {
        const response = await apiClient.put('/edit-user-profile', userData)
        
        if (response.data.success) {
          commit('UPDATE_USER', response.data.data.user)
          return response.data
        }
        
        throw new Error(response.data.message || 'Profile update failed')
      } catch (error) {
        throw error
      }
    },
    
    updateUser({ commit }, user) {
      commit('UPDATE_USER', user)
    },
    
    initializeAuth({ commit, state }) {
      // Initialize auth state from localStorage on app start
      if (state.token) {
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${state.token}`
      }
    }
  }
}